import React from 'react';
import {makeStyles} from '@material-ui/core';
import {Column} from 'react-table';
import {TFunction} from 'react-i18next';
import classNames from 'classnames';
import {Colors} from '../../styles/Colors';
import {RingGroupType} from '../../store/types/RingGroup';
import {RowData} from '../../components/DataGrid/types';
import SwitchWithLabel from '../../components/SwitchWithLabel/SwitchWithLabel';
import OverflowTooltip from '../../components/OverflowTooltip/OverflowTooltip';
import {ReactComponent as StrategyRandom} from '../../assets/strategyRandom.svg';
import {ReactComponent as StrategyLeastUsed} from '../../assets/strategyLeastUsed.svg';
import {ReactComponent as StrategySimultaneous} from '../../assets/strategySimultaneous.svg';
import {ReactComponent as StrategyOrder} from '../../assets/strategyOrder.svg';
import {ReactComponent as CheckIcon} from '../../assets/check.svg';
import {getDurationFromSec} from '../../utils/transformers';

export const useStyles = makeStyles(() => ({
    mainDashboardContainer: {
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: Colors.White,
        overflow: 'hidden',
        position: 'relative',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: 'calc(100% - 54px - 54px)',
        padding: '24px 96px',
    },
    rightSection: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    scrollable: {
        overflowY: 'auto',
        flex: 1,
        padding: '50px 90px 120px 90px',
        background: Colors.SmokeBackground,
    },
    contentContainer: {
        width: 1040,
        maxWidth: '100%',
    },
    filtersForm: {
        marginBottom: 24,
    },
    itemsContainer: {
        display: 'flex',
        alignItems: 'flex-start',

        '& .MuiFormControl-root': {
            marginRight: 15,
            width: 209,
        },
    },
    button: {
        '& .MuiButton-label': {
            color: Colors.White,
        },
        marginTop: 10,
    },
    table: {
        '& .MuiTableBody-root .MuiTableRow-root': {
            minHeight: 66,
            alignItems: 'center',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
            alignSelf: 'center',
        },
        '& thead th:first-child, & .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:first-child': {
            paddingLeft: '40px !important' as '40px',
        },
        '& thead th': {
            textAlign: 'center !important' as 'center',
        },
        '& thead th:nth-child(1), & thead th:nth-child(2), & thead th:nth-child(3), & thead th:nth-child(4)': {
            textAlign: 'left !important' as 'left',
        },
    },
    cell: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
    numberCell: {
        color: Colors.Text,
        fontFamily: 'Roboto',
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '28px',
        fontFeatureSettings: "'liga' off, 'clig' off",
    },
    nameCell: {
        color: Colors.Text,
        fontFamily: 'Roboto',
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        fontFeatureSettings: "'liga' off, 'clig' off",
    },
    statusSwitchRoot: {
        height: 'auto !important' as 'auto',
        marginBottom: '0 !important' as '0',
        marginLeft: -12,
    },
    statusSwitchLabel: {
        fontFamily: 'Roboto !important' as 'Roboto',
        fontSize: '16px !important' as '16px',
        fontStyle: 'normal',
        fontWeight: '400 !important' as any,
        lineHeight: 'normal',
        color: Colors.Text + ' !important' as any,
        fontFeatureSettings: "'liga' off, 'clig' off",
    },
    cellCentered: {
        justifyContent: 'center',
    },
    strategy: {
        fontSize: 12,
        fontWeight: 400,
        color: '#111111',
    },
    marginFromIcon: {
        marginLeft: 8,
    },
    dash: {
        color: Colors.Gray9,
    },
}));

export const formatWrapUpTime = (seconds?: number): string => {
    if (!seconds) return '—';
    return getDurationFromSec(seconds).string;
};

export const generateColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    onLoginToggle: (rg: RingGroupType, value: boolean) => void,
): Column<RowData<RingGroupType>>[] => [
    {
        Header: t<string>('common:number'),
        accessor: 'id',
        Cell: function Cell(params) {
            return (
                <div className={classNames(classes.cell, classes.numberCell)}>
                    {params.row.original.id}
                </div>
            );
        },
        width: 120,
        minWidth: 120,
    },
    {
        Header: t<string>('common:name'),
        accessor: 'name',
        Cell: function Cell(params) {
            return (
                <div className={classNames(classes.cell, classes.nameCell)}>
                    <OverflowTooltip
                        dataQa="account-rg-name"
                        text={params.row.original.name}
                        tooltip={params.row.original.name}
                        copy={false}
                    />
                </div>
            );
        },
        width: 220,
        minWidth: 180,
    },
    {
        Header: t<string>('common:status'),
        accessor: 'assigned_extensions',
        Cell: function Cell(params) {
            const rg = params.row.original;
            const isOn =
                (rg?.assigned_extensions?.[0]?.hunt_active ?? 'N') === 'Y';
            return (
                <div className={classes.cell}>
                    <SwitchWithLabel
                        id={`account-rg-logged-${rg.id}`}
                        field={`account-rg-logged-${rg.id}`}
                        dataQa={`account-rg-logged-${rg.id}`}
                        className={classes.statusSwitchRoot}
                        customLabelClass={classes.statusSwitchLabel}
                        label={t<string>(
                            isOn
                                ? 'screens:accountDashboard.loggedIn'
                                : 'screens:accountDashboard.loggedOut',
                        )}
                        value={isOn}
                        setValue={(_, value) => onLoginToggle(rg, value)}
                    />
                </div>
            );
        },
        width: 180,
        minWidth: 180,
    },
    {
        Header: (
            <div style={{width: '100%', textAlign: 'left'}}>
                {t<string>('screens:ringGroups.ringStrategy')}
            </div>
        ),
        accessor: 'hunt_sequence',
        Cell: function Cell(params) {
            const code = params.row.original.hunt_sequence;
            const value = t('enums:ringStrategy.' + code, code);
            return (
                <div
                    className={classNames(classes.cell, classes.strategy)}
                >
                    {code === 'Order' && <StrategyOrder />}
                    {code === 'Random' && <StrategyRandom />}
                    {code === 'Simultaneous' && <StrategySimultaneous />}
                    {code === 'LeastUsed' && <StrategyLeastUsed />}
                    <OverflowTooltip
                        dataQa="account-rg-strategy"
                        text={value}
                        tooltip={value}
                        copy={false}
                        classes={{text: classes.marginFromIcon}}
                    />
                </div>
            );
        },
        width: 160,
        minWidth: 140,
    },
    {
        Header: (
            <div style={{width: '100%', textAlign: 'left'}}>
                {t<string>('screens:calls.wrapupTime')}
            </div>
        ),
        accessor: 'call_wrap_up_time',
        Cell: function Cell(params) {
            const value = params.row.original.call_wrap_up_time;
            return (
                <div className={classes.cell}>
                    {formatWrapUpTime(value)}
                </div>
            );
        },
        width: 160,
        minWidth: 140,
    },
    {
        Header: t<string>('screens:addToRingGroup.ignoreFollowMe'),
        accessor: 'i_c_group',
        Cell: function Cell(params) {
            const huntstop =
                params.row.original?.assigned_extensions?.[0]?.huntstop;
            return (
                <div className={classNames(classes.cell, classes.cellCentered)}>
                    {huntstop === 'Y' ? (
                        <CheckIcon />
                    ) : (
                        <span className={classes.dash}>—</span>
                    )}
                </div>
            );
        },
        width: 160,
        minWidth: 140,
    },
];